import React, { useState, useEffect, useCallback } from "react";
import { btn } from "../../../shared/styles/buttonStyles";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { ChevronRightIcon } from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import { migrationApplication, migrationCreatePath, migrationDetailPath } from "../migrationRoutes";
import ActivityLogTable, { ActivityLogTableItem } from "../../../shared/ui/ActivityLogTable";
import FeatureTabs, { FeatureTab } from "../../../shared/ui/FeatureTabs";
import FeatureInformationPanel from "../../../shared/ui/FeatureInformationPanel";
import { PaginatedResponse } from "../../../shared/types/pagination";
import { formatAuditDate, formatAuditTime24Hour } from "../../../shared/utils/formatAuditTime24Hour";
import { formatActivityDuration } from "../../../shared/utils/formatActivityDuration";
import { ACTIVITY_API_PAGE_SIZE, ACTIVITY_LOG_PAGE_SIZE, ACTIVITY_LOG_POLL_INTERVAL_MS, ActivityFeedEntry, ExecutionActivityEntry, formatActivityValue, isExecutionActive, mapExecutionFeedEntries, parseActivityDetailsJson, sortActivityFeedEntries } from "../../../shared/utils/activityFeed";
import { getAccessToken } from "../../../shared/auth/getAccessToken";
import { MigrationStatusIcon, isMigrationRunning } from "../migrationStatus";

type MigrationRow = {
  rowKey: string;
  partitionKey: string;
  createdAt: string;
  instanceId: string;
  schoolCode: string;
  status: number;
};

const extractChangeLines = (detailsJson?: string): string[] => {
  const parsed = parseActivityDetailsJson(detailsJson);
  if (!parsed) {
    return [];
  }

  const lines: string[] = [];
  if (parsed.instanceId) lines.push(`Instance ID: ${formatActivityValue(parsed.instanceId)}`);
  if (parsed.schoolCode) lines.push(`School Code: ${formatActivityValue(parsed.schoolCode)}`);
  if (parsed.server) lines.push(`Server: ${formatActivityValue(parsed.server)}`);
  if (parsed.migrationId) lines.push(`Migration ID: ${formatActivityValue(parsed.migrationId)}`);
  return lines;
};

const MigrationList = () => {
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();

  // null until the first response, so the empty state never shows while loading
  const [migrations, setMigrations] = useState<MigrationRow[] | null>(null);
  const [activityLogs, setActivityLogs] = useState<ActivityFeedEntry[]>([]);
  const [activityLogsLoading, setActivityLogsLoading] = useState(false);
  const [activityLogsPage, setActivityLogsPage] = useState(1);
  const [activityLogsTotalPages, setActivityLogsTotalPages] = useState(1);
  const [activityLogsTotalCount, setActivityLogsTotalCount] = useState(0);
  const [hasActiveExecutions, setHasActiveExecutions] = useState(false);
  const [activeTab, setActiveTab] = useState<FeatureTab>("operations");
  const apiUrl = process.env.REACT_APP_API_URL;

  const fetchData = useCallback(async (quiet = false) => {
    try {
      const accessToken = await getAccessToken();
      const response = await axios.get(`${apiUrl}/migration/list/${migrationApplication}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      setMigrations(response.data.migrations as MigrationRow[]);
    } catch (ex) {
      if (!quiet) {
        toast.error("Failed to load migrations.");
        setMigrations((current) => current ?? []);
      }
    }
  }, [apiUrl]);

  // quiet = background poll: no loading state, no toast, keep the last rows on failure
  const fetchActivityLogs = useCallback(async (page = 1, quiet = false) => {
    try {
      if (!quiet) {
        setActivityLogsLoading(true);
      }
      const accessToken = await getAccessToken();

      // Each create writes an execution row the worker keeps updated, so it covers the whole feed
      const executionItems = await axios.get<PaginatedResponse<ExecutionActivityEntry>>(`${apiUrl}/migration/executions/${migrationApplication}`, {
        params: { pageSize: ACTIVITY_API_PAGE_SIZE },
        headers: { Authorization: `Bearer ${accessToken}` },
      }).then((response) => response.data.items);
      const feed = sortActivityFeedEntries(mapExecutionFeedEntries(executionItems));
      const totalCount = feed.length;
      const totalPages = Math.max(1, Math.ceil(totalCount / ACTIVITY_LOG_PAGE_SIZE));
      const safePage = Math.min(Math.max(page, 1), totalPages);

      setActivityLogs(feed.slice(
        (safePage - 1) * ACTIVITY_LOG_PAGE_SIZE,
        safePage * ACTIVITY_LOG_PAGE_SIZE,
      ));
      setActivityLogsPage(safePage);
      setActivityLogsTotalPages(totalPages);
      setActivityLogsTotalCount(totalCount);
      setHasActiveExecutions(executionItems.some((item) => isExecutionActive(item.status)));
    } catch (ex) {
      if (quiet) {
        return;
      }
      toast.error("Failed to load activity logs.");
      setActivityLogs([]);
      setActivityLogsPage(1);
      setActivityLogsTotalPages(1);
      setActivityLogsTotalCount(0);
      setHasActiveExecutions(false);
    } finally {
      if (!quiet) {
        setActivityLogsLoading(false);
      }
    }
  }, [apiUrl]);

  useEffect(() => {
    if (instance && accounts.length > 0) {
      void fetchData();
      void fetchActivityLogs(1);
    }
  }, [instance, accounts, fetchData, fetchActivityLogs]);

  // Keep status live while a migration is queued or running, like the other pages
  const hasActiveMigrations = hasActiveExecutions
    || (migrations ?? []).some((migration) => isMigrationRunning(migration.status, migration.createdAt));

  useEffect(() => {
    if (!hasActiveMigrations) {
      return;
    }

    const intervalId = window.setInterval(() => {
      void fetchData(true);
      void fetchActivityLogs(activityLogsPage, true);
    }, ACTIVITY_LOG_POLL_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [hasActiveMigrations, activityLogsPage, fetchData, fetchActivityLogs]);

  const activityLogRows: ActivityLogTableItem[] = activityLogs.map((log) => ({
    id: log.id,
    dateText: formatAuditDate(log.startedAtUtc),
    timeText: formatAuditTime24Hour(log.startedAtUtc),
    durationText: formatActivityDuration(log.durationMs),
    runningAtUtc: log.runningAtUtc,
    actionText: "Create Migration",
    byName: log.actorName || "Unknown",
    byEmail: log.actorEmail,
    status: log.status,
    changeLines: extractChangeLines(log.detailsJson),
    errorMessage: log.errorMessage,
    pipelineRunUrl: log.pipelineRunUrl,
  }));

  return (
    <div className="feature-workspace">
      <FeatureTabs activeTab={activeTab} onChange={setActiveTab} />

      {activeTab === "information" && (
        <FeatureInformationPanel summary="Tracks migration requests and their status.">
          <h3 className="text-sm font-semibold text-slate-800">Migration</h3>
          <ul className="mt-3 list-disc space-y-1 pl-4 text-xs leading-5 text-slate-600">
            <li>Each row shows a migration request and its latest status.</li>
            <li>Open a row to see task steps and logs.</li>
            <li>Use <span className="font-semibold">Create Migration</span> to submit a new request. Its Information tab has the steps.</li>
            <li>Execution: Background queues.</li>
          </ul>
        </FeatureInformationPanel>
      )}

      {activeTab === "operations" && (
        <div className="mt-2" id="feature-panel-operations" role="tabpanel" aria-labelledby="feature-tab-operations" tabIndex={0}>
          <section className="migration-list workspace-card w-full overflow-hidden border border-slate-200 bg-white px-5 py-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-sm font-bold leading-5 text-slate-800">Migration Requests</h3>
                <p className="mt-1 text-[11px] leading-4 text-slate-500">Open a row to see task steps and logs.</p>
              </div>
              <button
                type="button"
                className={btn.applySmall}
                onClick={() => navigate(migrationCreatePath)}
              >
                Create Migration
              </button>
            </div>
            <hr className="my-3 border-slate-200" />
            {migrations === null ? (
              <p className="px-3 py-6 text-center text-xs text-slate-500">Loading migrations...</p>
            ) : migrations.length === 0 ? (
              <div className="rounded-sm border border-dashed border-slate-300 bg-white p-8 text-center text-xs text-slate-500">
                No migrations found yet.
              </div>
            ) : (
              <ul className="divide-y divide-slate-200">
                {migrations.map((migration) => (
                  <MigrationItem
                    key={migration.rowKey}
                    migration={migration}
                    onOpen={() => navigate(migrationDetailPath(migration.rowKey))}
                  />
                ))}
              </ul>
            )}
          </section>

          <ActivityLogTable
            title="Activity Log"
            description="Tracks migration request activity."
            items={activityLogRows}
            loading={activityLogsLoading}
            page={activityLogsPage}
            totalPages={activityLogsTotalPages}
            totalCount={activityLogsTotalCount}
            onRefresh={() => fetchActivityLogs(activityLogsPage)}
            onPageChange={(pageNumber) => fetchActivityLogs(pageNumber)}
          />
        </div>
      )}
    </div>
  );
};

// The whole row is one button so it opens with a click, Enter or Space
const MigrationItem = ({ migration, onOpen }: { migration: MigrationRow; onOpen: () => void }) => (
  <li>
    <button
      type="button"
      onClick={onOpen}
      className="group/item flex w-full items-center justify-between rounded-sm px-3 py-3 text-left transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-slate-500"
    >
      <span className="flex min-w-0 gap-3">
        <MigrationStatusIcon status={migration.status} />
        <span className="min-w-0">
          <span className="block text-xs font-semibold text-slate-800">{migration.partitionKey}</span>
          <span className="mt-0.5 block text-[11px] text-slate-500">
            {`${formatAuditDate(migration.createdAt)} ${formatAuditTime24Hour(migration.createdAt)}`}
          </span>
        </span>
        <span className="min-w-0">
          <span className="block text-xs font-semibold text-slate-800">#{migration.rowKey}</span>
          <span className="mt-0.5 block text-[11px] text-slate-500">
            <span className="mr-2">Instance ID: {migration.instanceId}</span>
            School Code: {migration.schoolCode}
          </span>
        </span>
      </span>
      <span className="invisible flex items-center whitespace-nowrap rounded-sm py-1 pl-3 pr-2 text-[11px] text-slate-500 group-hover/item:visible group-focus-visible/item:visible">
        <span className="font-semibold">View</span>
        <ChevronRightIcon className="h-4 w-4 text-slate-700" aria-hidden="true" />
      </span>
    </button>
  </li>
);

export default MigrationList;
